// The Wygląd panel's logic (character look, 2026-10-29), Phaser-free: the tabs, each tab's choices, how many fit a
// row, a pick, and what "Gotowe" sends. LookPanel.js draws it.
import { LOOK_CATALOG, resolveLook } from '../look/lookCatalog.mjs';

export const LOOK_TABS = ['Skóra', 'Oczy', 'Fryzura', 'Kolor włosów'];
const FIELD = { 'Skóra': 'skin', 'Oczy': 'eyes', 'Fryzura': 'hairstyle', 'Kolor włosów': 'hairColor' };

/** The look field a tab edits. */
export const fieldOf = (tab) => FIELD[tab];

/** The choices of a tab for a body: [{id, colour}] (colour: the swatch; a hairstyle tile has none). */
export function optionsFor(tab, character) {
  switch (fieldOf(tab)) {
    case 'skin': return LOOK_CATALOG.skin.map((e) => ({ id: e.id, colour: e.mid }));
    case 'eyes': return LOOK_CATALOG.eyes.map((e) => ({ id: e.id, colour: e.light }));
    case 'hairColor': return LOOK_CATALOG.hairColor.map((e) => ({ id: e.id, colour: e.mid }));
    case 'hairstyle': return (LOOK_CATALOG.hairstyles[character] ?? []).map((e) => ({ id: e.id, colour: null }));
    default: return [];
  }
}

/** Swatches in one row: fewer, bigger ones on a phone (finger-sized), up to 8 on a computer. */
export function swatchesPerRow({ width, touch }) {
  if (touch || width <= 600) return 4;
  return 8;
}

const TILE_GAP = 4;
/** The ‹ › buttons' size (gameUi pageArrows). */
export const PAGE_ARROW = 64;

/** The hairstyle row: a tile's card is taller than a row step, so tiles never wrap (plan 3 Task 14: a second row
 *  covered the first and the buttons). {perPage, pages, step, arrows, halfSpan}: every tile when they fit `innerW`,
 *  else balanced pages with ‹ › either side, `halfSpan` from the row's middle (gameUi pageArrows). */
export function tileRow({ count, cardW, innerW }) {
  const step = cardW + TILE_GAP;
  if (count * step - TILE_GAP <= innerW) return { perPage: count, pages: 1, step, arrows: false, halfSpan: 0 };
  const most = Math.max(1, Math.floor((innerW - 2 * (PAGE_ARROW + TILE_GAP) + TILE_GAP) / step));
  const pages = Math.ceil(count / most), perPage = Math.ceil(count / pages);
  return { perPage, pages, step, arrows: true, halfSpan: (perPage * step - TILE_GAP) / 2 + TILE_GAP + PAGE_ARROW / 2 };
}

/** The page of `perPage` that holds `id` (the first when none does). */
export const pageOf = (ids, id, perPage) => Math.max(0, Math.floor(ids.indexOf(id) / perPage));

/** The look with this tab's field set to `id` (a new object). */
export function pick(look, tab, id) {
  return { ...(look ?? {}), [fieldOf(tab)]: id };
}

/** What "Gotowe" sends: only the fields that differ from the body's defaults; null when nothing does. */
export function doneLook(character, look) {
  const d = resolveLook(character, null), r = resolveLook(character, look);
  const out = {};
  for (const k of Object.keys(r)) if (r[k] !== d[k]) out[k] = r[k];
  return Object.keys(out).length ? out : null;
}

const TURN_STOPS = 8;           // one a compass step; the back sits at both ends
const unit = (t) => Math.min(1, Math.max(0, t));

/** The preview's turn slider (owner 2026-11-01, instead of the slow spin): `t` from 0 (left end) to 1 (right end) →
 *  the compass facing (dir8: E=0 clockwise). The middle faces the camera; sliding right turns her face to the right,
 *  left to the left; both ends show her back. */
export function facingAt(t) {
  const step = Math.round((unit(t) - 0.5) * TURN_STOPS);
  return (((2 - step) % 8) + 8) % 8;
}

/** Where the slider settles when let go: the nearest of its stops. */
export const snapTurn = (t) => Math.round(unit(t) * TURN_STOPS) / TURN_STOPS;

// Local-only tools under the preview (owner 2026-11-01, dev builds): a random look, the walk held still and stepped a
// frame at a time, the pose copied exactly (for a report).

/** A look with every field picked at random from its tab's choices (`rand`: 0 ≤ r < 1, Math.random by default). */
export function randomLook(character, rand = Math.random) {
  return Object.fromEntries(LOOK_TABS.map((tab) => {
    const opts = optionsFor(tab, character);
    return [fieldOf(tab), opts[Math.floor(rand() * opts.length)].id];
  }));
}

const WALK_FRAMES = 8;          // a walk row's cycle (walkAnim WALK.cols)

/** The walk frame `by` steps on from column `col`, round the cycle either way. */
export const stepFrame = (col, by) => (((col + by) % WALK_FRAMES) + WALK_FRAMES) % WALK_FRAMES;

/** What "Kopiuj" puts on the clipboard: the look, the clothes, the facing and what the body sheet shows (`shown`:
 *  NatalkaSprite.shownFrame — stand or walk, the row, mirrored or not, the sheet's frame number), as one JSON line. */
export function poseRecord({ character, look, equipped, facing8, shown }) {
  const { pose, row, flip, frame } = shown ?? {};
  return JSON.stringify({ character, look, equipped: equipped ?? null, facing8, pose, row, flip, frame });
}
